import L from "leaflet";

export type SavedSpotLayerRecord = {
  id: string;
  lat: number;
  lng: number;
  kind: string;
  name?: string;
  note?: string;
  createdAt?: number;
  [key: string]: any;
};

type MarkerCacheEntry = { marker: any; signature: string };

type UpdateArgs = {
  map: any;
  layer: any;
  spots: SavedSpotLayerRecord[];
  visible: boolean;
  activeFilter: string;
  selectedSpotId: string | null;
  dark: boolean;
  createSpotIcon: (spot: any, selected: boolean) => any;
  createClusterIcon: (count: number, kinds: any[]) => any;
  getSpotPopupHtml: (spot: any, expanded?: boolean) => string;
  getClusterPopupHtml: (spots: any[]) => string;
  focusPopup: (map: any, marker: any, node: HTMLElement, opts: { preferredGap: number; focusBias: number }) => void;
  onBeforeMarkerOpen: (opts?: { clearEditing?: boolean }) => void;
  onEdit: (spot: any) => void;
  onShare: (spot: any) => void | Promise<void>;
  onDelete: (spot: any) => void;
  closeTournamentTooltips?: () => void;
};

export type SavedSpotsLayerController = {
  schedule: (args: UpdateArgs, force?: boolean) => void;
  update: (args: UpdateArgs, force?: boolean) => void;
  forEachMarker: (fn: (marker: any) => void) => void;
  dispose: (layer?: any) => void;
};

export const createSavedSpotsLayerController = (): SavedSpotsLayerController => {
  const markerCache = new Map<string, MarkerCacheEntry>();
  let lastSignature = "";
  let raf: number | null = null;

  const forEachMarker = (fn: (marker: any) => void) => {
    markerCache.forEach((entry) => fn(entry.marker));
  };

  const clearMarkers = (layer: any) => {
    markerCache.forEach((entry) => {
      try { layer?.removeLayer?.(entry.marker); } catch {}
    });
    markerCache.clear();
  };

  const update = (args: UpdateArgs, force = false) => {
    const { map, layer } = args;
    if (!map || !layer) return;

    if (!args.visible) {
      if (force || lastSignature !== "hidden") {
        clearMarkers(layer);
        lastSignature = "hidden";
      }
      return;
    }

    const source = args.spots.filter(
      (spot) => args.activeFilter === "all" || spot.kind === args.activeFilter,
    );
    const renderItems = source.map((spot) => ({
      latLng: { lat: spot.lat, lng: spot.lng },
      count: 1,
      spots: [spot],
      kinds: [spot.kind],
    }));

    const signature = JSON.stringify({
      dark: args.dark,
      zoom: Math.round(Number(map?.getZoom?.() ?? 13) * 10) / 10,
      selectedSpotId: args.selectedSpotId || "",
      activeFilter: args.activeFilter,
      visible: args.visible,
      items: renderItems.map((item) => ({
        count: item.count,
        lat: Number(item.latLng.lat || 0).toFixed(5),
        lng: Number(item.latLng.lng || 0).toFixed(5),
        ids: item.spots.map((spot) => String(spot.id || "")).sort(),
      })),
    });

    if (!force && signature === lastSignature) return;
    const nextKeys = new Set<string>();

    renderItems.forEach((item) => {
      if (!item.spots.length) return;
      const ids = item.spots.map((spot) => String(spot.id || "")).sort();
      const markerKey = item.count > 1 ? `cluster:${ids.join("|")}` : `spot:${ids[0]}`;
      const selected = item.count === 1 && args.selectedSpotId === ids[0];
      const markerLatLng = { lat: Number(item.latLng.lat), lng: Number(item.latLng.lng) };
      const itemSignature = JSON.stringify({
        markerKey,
        count: item.count,
        lat: markerLatLng.lat.toFixed(6),
        lng: markerLatLng.lng.toFixed(6),
        selected,
        dark: args.dark,
        kinds: item.kinds,
        names: item.spots.map((spot) => String(spot.name || "")),
      });
      nextKeys.add(markerKey);

      const cached = markerCache.get(markerKey);
      if (cached && cached.signature === itemSignature) {
        try { cached.marker.setLatLng?.([markerLatLng.lat, markerLatLng.lng]); } catch {}
        return;
      }
      if (cached) {
        try { layer.removeLayer?.(cached.marker); } catch {}
        markerCache.delete(markerKey);
      }

      if (item.count > 1) {
        const marker = (L as any).marker([markerLatLng.lat, markerLatLng.lng], {
          icon: args.createClusterIcon(item.count, item.kinds),
          pane: "markersPane",
          keyboard: false,
          riseOnHover: true,
          zIndexOffset: 980,
        });
        marker.bindPopup(args.getClusterPopupHtml(item.spots), {
          autoPan: false,
          closeButton: false,
          className: "roddo-saved-spot-inline-popup",
          offset: [0, -24],
          maxWidth: 270,
        });
        marker.on("popupopen", (event: any) => {
          window.setTimeout(() => {
            try {
              const node = event?.popup?.getElement?.() as HTMLElement | null;
              if (node) args.focusPopup(map, marker, node, { preferredGap: 24, focusBias: 0.68 });
            } catch {}
          }, 0);
        });
        marker.on("click", () => {
          args.onBeforeMarkerOpen();
          args.closeTournamentTooltips?.();
          try { marker.openPopup?.(); } catch {}
        });
        marker.addTo(layer);
        markerCache.set(markerKey, { marker, signature: itemSignature });
        return;
      }

      const spot = item.spots[0];
      const marker = (L as any).marker([markerLatLng.lat, markerLatLng.lng], {
        icon: args.createSpotIcon(spot, selected),
        pane: "markersPane",
        keyboard: false,
        riseOnHover: true,
        zIndexOffset: selected ? 1600 : 920,
      });
      let popupExpanded = false;
      marker.bindPopup(args.getSpotPopupHtml(spot, popupExpanded), {
        autoPan: false,
        closeButton: false,
        className: "roddo-saved-spot-inline-popup",
        offset: [0, -24],
        maxWidth: 290,
      });

      const bindActions = () => {
        window.setTimeout(() => {
          const popupEl = marker.getPopup?.()?.getElement?.() as HTMLElement | null;
          if (!popupEl) return;
          popupEl.querySelectorAll<HTMLElement>("[data-saved-spot-action]").forEach((button) => {
            button.onclick = (event) => {
              event.preventDefault();
              event.stopPropagation();
              const action = button.getAttribute("data-saved-spot-action");
              if (action === "toggle") {
                popupExpanded = !popupExpanded;
                marker.setPopupContent?.(args.getSpotPopupHtml(spot, popupExpanded));
                marker.getPopup?.()?.update?.();
                bindActions();
                window.setTimeout(() => {
                  const node = marker.getPopup?.()?.getElement?.() as HTMLElement | null;
                  if (node) args.focusPopup(map, marker, node, { preferredGap: 24, focusBias: popupExpanded ? 0.78 : 0.68 });
                }, 0);
                return;
              }
              if (action === "edit") {
                try { marker.closePopup?.(); } catch {}
                args.onEdit(spot);
                return;
              }
              if (action === "share") {
                void args.onShare(spot);
                return;
              }
              if (action === "delete") {
                try { marker.closePopup?.(); } catch {}
                args.onDelete(spot);
              }
            };
          });
        }, 0);
      };

      marker.on("popupopen", (event: any) => {
        bindActions();
        window.setTimeout(() => {
          try {
            const node = event?.popup?.getElement?.() as HTMLElement | null;
            if (node) args.focusPopup(map, marker, node, { preferredGap: 24, focusBias: popupExpanded ? 0.78 : 0.68 });
          } catch {}
        }, 0);
      });
      marker.on("popupclose", () => { popupExpanded = false; });
      marker.on("click", () => {
        args.onBeforeMarkerOpen({ clearEditing: true });
        args.closeTournamentTooltips?.();
        try {
          marker.setPopupContent?.(args.getSpotPopupHtml(spot, false));
          marker.openPopup?.();
        } catch {}
      });
      marker.addTo(layer);
      markerCache.set(markerKey, { marker, signature: itemSignature });
    });

    Array.from(markerCache.keys()).forEach((key) => {
      if (nextKeys.has(key)) return;
      const cached = markerCache.get(key);
      if (cached) {
        try { layer.removeLayer?.(cached.marker); } catch {}
      }
      markerCache.delete(key);
    });

    lastSignature = signature;
  };

  const schedule = (args: UpdateArgs, force = false) => {
    if (raf != null) window.cancelAnimationFrame(raf);
    raf = window.requestAnimationFrame(() => {
      raf = null;
      update(args, force);
    });
  };

  const dispose = (layer?: any) => {
    if (raf != null) {
      window.cancelAnimationFrame(raf);
      raf = null;
    }
    clearMarkers(layer);
    lastSignature = "";
  };

  return { schedule, update, forEachMarker, dispose };
};
